import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography, Chip, Alert, CircularProgress } from '@mui/material';
import { Mic, Stop } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Deepgram Voice Agent websocket. The browser connects directly (low latency)
// using a short-lived token minted by our backend; the long-lived API key never
// reaches the client.
const AGENT_WS = 'wss://agent.deepgram.com/v1/agent/converse';
const OUTPUT_RATE = 24000; // we ask the agent to speak linear16 @ 24kHz

interface Turn {
  role: string;
  text: string;
}

interface Props {
  /** Interview job to ground the interviewer in (the backend builds the prompt
   *  from this lead's job spec + resume). Omit for a general practice run. */
  leadId?: string;
  /** Fallback system prompt if the server doesn't return a grounded one. */
  systemPrompt?: string;
  /** Fallback greeting. */
  greeting?: string;
  /** Called after a session is billed, so the parent can refresh the balance. */
  onBilled?: () => void;
  /** Show the "Past interviews" list (default true). Off for per-job embeds so
   *  the list isn't duplicated across cards. */
  showHistory?: boolean;
}

type Status = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

/**
 * SPIKE: real-time voice mock interview over Deepgram's Voice Agent API.
 *
 * Captures the mic, streams linear16 PCM to the agent, and plays the agent's
 * audio back (with barge-in: when the user starts speaking we stop the agent's
 * playback immediately). This is a thin vertical slice to validate the audio +
 * websocket plumbing end-to-end; per-job grounding, billing, and polish come in
 * the full build. Requires DEEPGRAM_API_KEY configured on the backend.
 *
 * Exact Deepgram field names (think.prompt, listen/speak model ids) are per the
 * current docs and are the main thing to confirm on first live run.
 */
interface Technique {
  name: string;
  explanation: string;
}

interface ImprovedAnswer {
  question: string;
  original: string;
  improved: string;
  why: string;
}

interface Review {
  overall_score: number | null;
  summary: string;
  strengths: string[];
  improvements: string[];
  recommendations: string[];
  techniques?: Technique[];
  improved_answers?: ImprovedAnswer[];
}

interface PastSession {
  id: string;
  job_title?: string | null;
  created_at: string;
  review: Review;
  transcript: Turn[];
}

const VoiceMockInterview: React.FC<Props> = ({
  leadId,
  systemPrompt,
  greeting,
  onBilled,
  showHistory = true,
}) => {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [review, setReview] = useState<Review | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [billed, setBilled] = useState<{ credits: number; minutes: number } | null>(null);
  const [pastSessions, setPastSessions] = useState<PastSession[]>([]);
  const startedAtRef = useRef<number>(0); // ms timestamp when the session went live

  const wsRef = useRef<WebSocket | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const procRef = useRef<ScriptProcessorNode | null>(null);
  // Scheduled playback sources + the time the next chunk should start, so audio
  // plays gaplessly; cleared on barge-in.
  const liveSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const nextStartRef = useRef<number>(0);

  const stopPlayback = () => {
    liveSourcesRef.current.forEach((s) => {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
    });
    liveSourcesRef.current = [];
    nextStartRef.current = 0;
  };

  const enqueueAudio = (ab: ArrayBuffer) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const int16 = new Int16Array(ab);
    const f32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 32768;
    const buf = ctx.createBuffer(1, f32.length, OUTPUT_RATE);
    buf.getChannelData(0).set(f32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime, nextStartRef.current);
    src.start(startAt);
    nextStartRef.current = startAt + buf.duration;
    liveSourcesRef.current.push(src);
    src.onended = () => {
      liveSourcesRef.current = liveSourcesRef.current.filter((s) => s !== src);
    };
  };

  const cleanup = () => {
    stopPlayback();
    try {
      procRef.current?.disconnect();
    } catch {
      /* noop */
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try {
      ctxRef.current?.close();
    } catch {
      /* noop */
    }
    if (wsRef.current && wsRef.current.readyState <= 1) wsRef.current.close();
    wsRef.current = null;
    ctxRef.current = null;
    streamRef.current = null;
    procRef.current = null;
  };

  // Load the user's saved past interviews (most recent first).
  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/interview/sessions`);
      setPastSessions(res.data?.sessions || []);
    } catch {
      /* none / not signed in — ignore */
    }
  };

  useEffect(() => {
    if (showHistory) loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Score the finished interview from its transcript (saved + billed server-side).
  const requestReview = async (transcript: Turn[]) => {
    if (transcript.length === 0) return;
    const durationSeconds = startedAtRef.current
      ? Math.round((Date.now() - startedAtRef.current) / 1000)
      : 0;
    setReviewLoading(true);
    setReviewError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/interview/voice-review`, {
        transcript,
        duration_seconds: durationSeconds,
        lead_id: leadId || null,
      });
      setReview(res.data);
      setCurrentSessionId(res.data?.id || null);
      if (typeof res.data?.credits_charged === 'number') {
        setBilled({ credits: res.data.credits_charged, minutes: res.data.voice_minutes || 0 });
      }
      loadSessions(); // the new session is now saved — refresh history
      onBilled?.(); // let the parent refresh the credit balance
    } catch (err: any) {
      setReviewError(
        err?.response?.data?.detail || 'Could not generate your interview review.'
      );
    } finally {
      setReviewLoading(false);
    }
  };

  // Show a saved past interview in the review + transcript panels.
  const viewSession = (s: PastSession) => {
    setReview(s.review);
    setCurrentSessionId(s.id);
    setTurns(s.transcript || []);
    setStatus('ended');
    setError('');
    setReviewError('');
    setBilled(null);
  };

  // Re-run the review for the displayed saved session (e.g. to pick up newer
  // review fields like techniques + model answers). Free.
  const regenerateReview = async () => {
    if (!currentSessionId) return;
    setReviewLoading(true);
    setReviewError('');
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/interview/sessions/${currentSessionId}/review`
      );
      setReview(res.data);
      if (typeof res.data?.credits_charged === 'number') {
        setBilled({ credits: res.data.credits_charged, minutes: 0 });
      }
      loadSessions();
      onBilled?.(); // refresh the header credit balance
    } catch (err: any) {
      const sc = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setReviewError(
        sc === 402
          ? "You're out of credits to regenerate the review — add a plan or top up on the Plans tab."
          : (typeof detail === 'string' ? detail : null) || 'Could not regenerate the review.'
      );
    } finally {
      setReviewLoading(false);
    }
  };

  const stop = () => {
    cleanup();
    setStatus('ended');
    requestReview(turns); // generate the post-interview review from the transcript
  };

  const start = async () => {
    setError('');
    setTurns([]);
    setReview(null);
    setReviewError('');
    setBilled(null);
    setCurrentSessionId(null);
    setStatus('connecting');
    try {
      // 1) Short-lived Deepgram token from our backend. Pass the interview job
      //    so the server returns a prompt grounded in that role + the resume.
      const tokenRes = await axios.post(`${API_BASE_URL}/api/interview/voice-token`, {
        lead_id: leadId || null,
      });
      const token: string = tokenRes.data?.access_token;
      if (!token) throw new Error('No voice token returned');
      // Server-built grounded prompt/greeting (fall back to props/defaults).
      const agentPrompt: string | undefined = tokenRes.data?.system_prompt || systemPrompt;
      const agentGreeting: string | undefined = tokenRes.data?.greeting || greeting;

      // 2) Mic + audio context.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AudioCtx: typeof AudioContext =
        window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      ctxRef.current = ctx;

      // 3) Connect to the agent (token passed via WS subprotocol — browsers
      //    can't set Authorization headers on a websocket).
      const ws = new WebSocket(AGENT_WS, ['bearer', token]);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: 'Settings',
            audio: {
              input: { encoding: 'linear16', sample_rate: Math.round(ctx.sampleRate) },
              output: { encoding: 'linear16', sample_rate: OUTPUT_RATE, container: 'none' },
            },
            agent: {
              listen: { provider: { type: 'deepgram', model: 'nova-3' } },
              think: {
                provider: { type: 'open_ai', model: 'gpt-4o' },
                prompt:
                  agentPrompt ||
                  'You are a friendly but rigorous job interviewer. Ask one question at a time, listen, ask brief follow-ups, and keep the conversation natural. Keep your turns short.',
              },
              speak: { provider: { type: 'deepgram', model: 'aura-2-thalia-en' } },
              greeting: agentGreeting || "Hi! Thanks for joining. Whenever you're ready, tell me a bit about yourself.",
            },
          })
        );

        // 4) Stream mic audio as linear16 PCM.
        const source = ctx.createMediaStreamSource(stream);
        const proc = ctx.createScriptProcessor(4096, 1, 1);
        procRef.current = proc;
        proc.onaudioprocess = (e: AudioProcessingEvent) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const input = e.inputBuffer.getChannelData(0);
          const pcm = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }
          ws.send(pcm.buffer);
        };
        source.connect(proc);
        // ScriptProcessor must be connected to the destination to run; route it
        // through a muted gain node so the mic isn't echoed to the speakers.
        const mute = ctx.createGain();
        mute.gain.value = 0;
        proc.connect(mute);
        mute.connect(ctx.destination);

        startedAtRef.current = Date.now();
        setStatus('live');
      };

      ws.onmessage = (evt: MessageEvent) => {
        if (typeof evt.data !== 'string') {
          enqueueAudio(evt.data as ArrayBuffer); // raw agent audio
          return;
        }
        try {
          const msg = JSON.parse(evt.data);
          switch (msg.type) {
            case 'UserStartedSpeaking':
              stopPlayback(); // barge-in: cut the agent off
              break;
            case 'ConversationText':
              if (msg.content) {
                setTurns((prev) => [...prev, { role: msg.role || 'agent', text: msg.content }]);
              }
              break;
            case 'Error':
              setError(msg.description || 'Voice agent error');
              break;
            default:
              break; // Welcome / SettingsApplied / AgentThinking / etc.
          }
        } catch {
          /* non-JSON text frame — ignore */
        }
      };

      ws.onerror = () => {
        setError('Voice connection error.');
        setStatus('error');
        cleanup();
      };
      ws.onclose = () => {
        setStatus((s) => (s === 'live' || s === 'connecting' ? 'ended' : s));
      };
    } catch (err: any) {
      const sc = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (sc === 402) {
        setError("You're out of credits for a voice mock interview — add a plan or top up on the Plans tab.");
      } else {
        setError(
          (typeof detail === 'string' ? detail : null) ||
            err?.message ||
            'Could not start the voice interview. Check mic permissions and that the feature is configured.'
        );
      }
      setStatus('error');
      cleanup();
    }
  };

  const live = status === 'live' || status === 'connecting';

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {!live ? (
          <Button variant="contained" startIcon={<Mic />} onClick={start}>
            Start voice mock interview
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="error"
            startIcon={status === 'connecting' ? <CircularProgress size={16} /> : <Stop />}
            onClick={stop}
          >
            {status === 'connecting' ? 'Connecting…' : 'End interview'}
          </Button>
        )}
        {status === 'live' && <Chip size="small" color="success" label="● Live" />}
        <Chip size="small" variant="outlined" label="Beta" />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Uses credits: ~1 per minute of conversation + 1 for the review.
      </Typography>

      {billed && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Charged {billed.credits} credit{billed.credits === 1 ? '' : 's'}
          {billed.minutes > 0 ? ` (${billed.minutes} min + review)` : ' (review)'}.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {reviewLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Scoring your interview…
          </Typography>
        </Stack>
      )}

      {reviewError && (
        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setReviewError('')}>
          {reviewError}
        </Alert>
      )}

      {review && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="h6">Interview review</Typography>
            {typeof review.overall_score === 'number' && (
              <Chip
                color={
                  review.overall_score >= 80
                    ? 'success'
                    : review.overall_score >= 60
                    ? 'warning'
                    : 'error'
                }
                label={`Score: ${review.overall_score}/100`}
              />
            )}
            {currentSessionId && (
              <Button
                size="small"
                onClick={regenerateReview}
                disabled={reviewLoading}
                startIcon={reviewLoading ? <CircularProgress size={14} /> : undefined}
              >
                Regenerate review
              </Button>
            )}
          </Stack>
          {review.summary && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {review.summary}
            </Typography>
          )}
          {([
            ['Strengths', review.strengths],
            ['Areas to improve', review.improvements],
            ['Recommendations', review.recommendations],
          ] as [string, string[]][]).map(
            ([label, items]) =>
              items &&
              items.length > 0 && (
                <Box key={label} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>
                    {label}
                  </Typography>
                  {items.map((it, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>
                      • {it}
                    </Typography>
                  ))}
                </Box>
              )
          )}

          {review.techniques && review.techniques.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="primary.main" gutterBottom>
                Techniques to use
              </Typography>
              {review.techniques.map((t, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t.name}:</strong> {t.explanation}
                </Typography>
              ))}
            </Box>
          )}

          {review.improved_answers && review.improved_answers.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="primary.main" gutterBottom>
                How to answer better
              </Typography>
              {review.improved_answers.map((a, i) => (
                <Box
                  key={i}
                  sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  {a.question && (
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {a.question}
                    </Typography>
                  )}
                  {a.original && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      You said: {a.original}
                    </Typography>
                  )}
                  <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
                    Stronger answer: {a.improved}
                  </Typography>
                  {a.why && (
                    <Typography variant="caption" color="text.secondary">
                      Why it scores better: {a.why}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {turns.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Transcript
          </Typography>
          {turns.map((t, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t.role === 'user' ? 'You' : 'Interviewer'}:</strong> {t.text}
            </Typography>
          ))}
        </Box>
      )}

      {showHistory && pastSessions.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Past interviews
          </Typography>
          <Stack spacing={0.5}>
            {pastSessions.map((s) => (
              <Stack
                key={s.id}
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Button size="small" variant="text" onClick={() => viewSession(s)}>
                  {new Date(s.created_at).toLocaleString()}
                  {s.job_title ? ` · ${s.job_title}` : ''}
                </Button>
                {typeof s.review?.overall_score === 'number' && (
                  <Chip size="small" variant="outlined" label={`${s.review.overall_score}/100`} />
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default VoiceMockInterview;
